import {
  Component,
  OnInit,
  OnDestroy,
  ElementRef,
  Input,
  inject,
  ChangeDetectionStrategy
} from '@angular/core';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import {
	AMBIENT_INTENSITY,
	CANVAS_SIZE,
	FILL_INTENSITY,
	KEY_INTENSITY,
	MODEL_SCALE,
	RIM_INTENSITY,
} from '@/app/core/models/collection/booster.constants';

@Component({
	selector: 'peps-booster-display',
	standalone: true,
	changeDetection: ChangeDetectionStrategy.Eager,
	template: `<div class="w-full flex justify-center"><div class="w-36 h-36 shrink-0 relative"></div></div>`,
})
export class BoosterDisplayComponent implements OnInit, OnDestroy {
	@Input() modelPath = 'assets/model/peps_classic.glb';

	private readonly el = inject(ElementRef);
	private renderer!: THREE.WebGLRenderer;
	private scene!: THREE.Scene;
	private camera!: THREE.PerspectiveCamera;
	private animFrameId: number | null = null;

	ngOnInit(): void {
		const wrap: HTMLElement = this.el.nativeElement.querySelector('div > div');

		this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
		this.renderer.setClearColor(0x000000, 0);
		this.renderer.setPixelRatio(window.devicePixelRatio);
		this.renderer.setSize(CANVAS_SIZE, CANVAS_SIZE);
		Object.assign(this.renderer.domElement.style, { display: 'block', width: '100%', height: '100%' });
		this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
		this.renderer.toneMappingExposure = 1.2;
		wrap.appendChild(this.renderer.domElement);

		this.scene = new THREE.Scene();
		this.camera = new THREE.PerspectiveCamera(45, 1, 0.1, 100);
		this.camera.position.set(0, 0, 5);

		this.scene.add(new THREE.AmbientLight(0xffffff, AMBIENT_INTENSITY));

		const keyLight = new THREE.DirectionalLight(0xffffff, KEY_INTENSITY);
		keyLight.position.set(3, 5, 3);
		this.scene.add(keyLight);

		const rimLight = new THREE.DirectionalLight(0xaabbff, RIM_INTENSITY);
		rimLight.position.set(-3, 2, -2);
		this.scene.add(rimLight);

		const fillLight = new THREE.DirectionalLight(0xffffff, FILL_INTENSITY);
		fillLight.position.set(0, 0, 5);
		this.scene.add(fillLight);

		new GLTFLoader().load(this.modelPath, (gltf) => {
			const model = gltf.scene;
			const box = new THREE.Box3().setFromObject(model);
			const center = new THREE.Vector3();
			box.getCenter(center);
			model.position.sub(center);
			const size = new THREE.Vector3();
			box.getSize(size);
			model.scale.setScalar(MODEL_SCALE / Math.max(size.x, size.y, size.z));
			// Légère rotation pour montrer le volume du booster
			model.rotation.y = Math.PI * 0.15;
			this.scene.add(model);
			this.renderer.render(this.scene, this.camera);
		});
	}

	ngOnDestroy(): void {
		if (this.animFrameId !== null) cancelAnimationFrame(this.animFrameId);
		this.renderer?.dispose();
	}
}
